import React from 'react';
import {scaleLinear, scaleTime} from 'd3-scale';
import PropTypes from 'prop-types';
import Chart from 'components/pure/charts/custom/Chart';
import Axes from 'components/pure/charts/custom/Axes';
import BarSeries from 'components/pure/charts/custom/BarSeries';
import compact from 'lodash/compact';
import AreaSeries from 'components/pure/charts/custom/AreaSeries';
import Guideline from 'components/pure/charts/custom/Guideline';
import GoalLabel from 'components/pure/charts/custom/GoalLabel';
import Theme, {flamingo} from 'styles/theme';
import './CoachingSkillVelocityChart.styl';
import * as queryParams from 'utils/queryParams';

const yMax = 100;
const yMin = 0;
const scaleMax = 5;
const normalizationFactor = yMax / scaleMax;

/**
 * Stateless container for coaching skill velocity chart. Internally uses d3 and svg to show user progress
 * through coaching reviews and scorecard scores.
 *
 * This parent obtains the score data and determines scales/extents that are used to pass down to the child components
 * to render data/axes/etc.
 */
export default class CoachingSkillVelocityChart extends React.PureComponent {
  extractAndNormalizeData() {
    const normalizedReviewData = this.props.reviewData.map(r => {
      return {
        id: r.reviewId,
        date: new Date(r.date),
        value: r.score,
      };
    });

    const normalizedScorecardData = compact(
      this.props.scorecardData.map(r => {
        return r.value ? {date: new Date(r.date), value: r.value * normalizationFactor} : null;
      })
    );

    return {
      ...this.props,
      reviewData: normalizedReviewData,
      scorecardData: normalizedScorecardData,
    };
  }

  render() {
    const {
      reviewData,
      skillBaseline,
      skillGoal,
      planStart,
      planEnd,
      currentReviewId,
      scorecardData,
    } = this.extractAndNormalizeData();

    const margins = {top: 5, right: 21, bottom: 25, left: 29};
    const svgDimensions = {width: 490, height: 150};

    const xScale = scaleTime()
      .domain([planStart, planEnd])
      .range([margins.left, svgDimensions.width - margins.right])
      .clamp(true);

    const yScale = scaleLinear()
      .domain([yMin, yMax])
      .range([svgDimensions.height - margins.bottom, margins.top]);

    const scales = {xScale, yScale};

    return (
      <Chart
        svgDimensions={svgDimensions}
        bordered={true}
        margins={margins}
        className="coaching-skill-velocity-chart">
        <AreaSeries
          scales={scales}
          margins={margins}
          className="scorecard-data"
          data={scorecardData}
          gradient={{
            id: 'averagePerformingGradient',
            direction: 'topToBottom',
            startOpacity: 1,
            endOpacity: 0.2,
            color: '#cfc3c9',
          }}
        />
        <Axes scales={scales} margins={margins} />
        <BarSeries
          scales={scales}
          margins={margins}
          data={reviewData}
          highlightedId={currentReviewId}
          onSelectBar={id => queryParams.set({reviewId: id})}
          fill={Theme.palette.darkBlueGrey}
          highlightFill={flamingo}
          barWidth={8}
          roundInPixels={15}
          placeholderForZeroPercentage={0.02}
        />
        {!isNaN(skillBaseline) && !isNaN(skillGoal) && (
          <Guideline
            scales={scales}
            xMin={planStart}
            xMax={planEnd}
            baseline={skillBaseline}
            goal={skillGoal}>
            <GoalLabel scales={scales} goal={skillGoal} />
          </Guideline>
        )}
      </Chart>
    );
  }
}

CoachingSkillVelocityChart.propTypes = {
  reviewData: PropTypes.array.isRequired,
  scorecardData: PropTypes.array.isRequired,
  planStart: PropTypes.instanceOf(Date),
  skillBaseline: PropTypes.number,
  skillGoal: PropTypes.number,
  selectedReviewIdx: PropTypes.number,
  onSelectReviewBar: PropTypes.func,
};
